import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Gauge,
  Locate,
  Phone,
  Clock,
  ShieldAlert,
  User,
  X,
  Briefcase,
  MapPin,
  Settings2,
  ChevronRight,
  ChevronDown,
} from 'lucide-react';
import dayjs from 'dayjs';
import { staffActions } from '@/store';
import { getStaffStatus } from '@/lib/getStaffStatus';
import useCoordinateFormat from '@/hooks/useCoordinateFormat';
import useUnits from '@/hooks/useUnits';
import useTelemetryPrefs from '@/hooks/useTelemetryPrefs';
import TelemetryCardPicker from '@/components/shared/TelemetryCardPicker';
import Tooltip from '@/components/shared/Tooltip';

// Stable card identity. Shared cardVisibility/cardOrder pref Record
// across vehicle + staff — keys that mean the same on both surfaces
// (battery, coordinates) intentionally share names so a user's
// toggle applies to both. Disjoint keys (role, shift) are
// staff-specific.
export const STAFF_CARD_KEYS = [
  'lastFix', 'coordinates', 'speed', 'shift', 'phone', 'role', 'sos',
];

const frost = () => ({
  background: 'var(--glass-bg)',
  backdropFilter: 'blur(40px) saturate(180%)',
  WebkitBackdropFilter: 'blur(40px) saturate(180%)',
});

function Card({ icon: Icon, label, value, unit, alert }) {
  const bgClass = alert
    ? 'bg-red-500/10 border border-red-300/40'
    : 'bg-white/40 border border-white/50 hover:bg-white/60';
  const iconClass = alert ? 'text-red-500' : 'text-slate-400';
  const textClass = alert ? 'text-red-700 dark:text-red-400' : 'text-slate-800 dark:text-slate-200';
  return (
    <div className={`rounded-xl p-2.5 transition-all ${bgClass}`}>
      <div className="flex items-center gap-1 mb-0.5">
        <Icon size={11} className={iconClass} />
        <span className="text-[9px] font-semibold text-slate-500 uppercase tracking-wider">{label}</span>
      </div>
      <p className={`text-sm font-bold ${textClass}`}>
        {value}
        {unit && <span className="text-[10px] font-normal text-slate-400 ms-0.5">{unit}</span>}
      </p>
    </div>
  );
}

function fmtAge(iso) {
  if (!iso) return '—';
  const d = dayjs(iso);
  const min = dayjs().diff(d, 'minute');
  if (min < 1) return 'now';
  if (min < 60) return `${min}m`;
  const hr = dayjs().diff(d, 'hour');
  if (hr < 24) return `${hr}h`;
  return d.format('MMM D');
}

function shiftSinceLabel(startedAt) {
  if (!startedAt) return '—';
  const since = dayjs().diff(dayjs(startedAt), 'minute');
  if (since < 60) return `${since}m`;
  return `${Math.floor(since / 60)}h ${since % 60}m`;
}

export default function StaffTelemetryPanel({ mode = 'floating' }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const fmtCoord = useCoordinateFormat();
  const units = useUnits();
  const prefs = useTelemetryPrefs();
  const [pickerOpen, setPickerOpen] = useState(false);

  const selectedUserId = useSelector((s) => s.staff?.selectedUserId);
  const row = useSelector((s) => (selectedUserId ? s.staff?.byUserId?.[selectedUserId] : null));

  useEffect(() => {
    if (!pickerOpen) return;
    const handler = (e) => {
      if (e.target.closest('[data-telemetry-picker-root]')) return;
      setPickerOpen(false);
    };
    window.addEventListener('mousedown', handler);
    return () => window.removeEventListener('mousedown', handler);
  }, [pickerOpen]);

  useEffect(() => {
    setPickerOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUserId]);

  // Auto-expand the docked panel when the user selects a different staff member.
  useEffect(() => {
    if (mode !== 'docked') return;
    if (selectedUserId) prefs.setCollapsed(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUserId]);

  if (!selectedUserId || !row) return null;

  const status = getStaffStatus(row);
  const close = () => dispatch(staffActions.clearSelection());

  const cardsRaw = [
    {
      key: 'lastFix',
      icon: Clock,
      label: t('people.col.lastFix', 'Last fix'),
      value: fmtAge(row.lastFix?.fixTime),
      unit: null,
      show: true,
    },
    {
      key: 'coordinates',
      icon: MapPin,
      label: t('telemetry.coordinates', 'Coords'),
      value: row.lastFix ? <bdi>{fmtCoord(row.lastFix.latitude, row.lastFix.longitude)}</bdi> : '—',
      unit: null,
      show: true,
    },
    {
      key: 'speed',
      icon: Gauge,
      label: t('telemetry.speed', 'Speed'),
      value: row.lastFix?.speedKmh != null ? units.speedFromKmh(Number(row.lastFix.speedKmh)).toFixed(1) : '—',
      unit: row.lastFix?.speedKmh != null ? units.speedLabel : null,
      show: row.lastFix?.speedKmh != null,
    },
    {
      key: 'shift',
      icon: Locate,
      label: t('people.col.shiftStarted', 'Shift'),
      value: row.shift ? shiftSinceLabel(row.shift.startedAt) : t('people.status.offShift', 'Off'),
      unit: row.shift ? 'ago' : null,
      show: true,
    },
    {
      key: 'phone',
      icon: Phone,
      label: t('people.col.phone', 'Phone'),
      value: row.phone ? <bdi>{row.phone}</bdi> : '—',
      unit: null,
      show: !!row.phone,
    },
    {
      key: 'role',
      icon: Briefcase,
      label: t('people.col.role', 'Role'),
      value: (row.staffRole ?? 'other').replace(/_/g, ' '),
      unit: null,
      show: true,
    },
    {
      key: 'sos',
      icon: ShieldAlert,
      label: t('people.col.status', 'SOS'),
      value: row.sos ? t('people.status.sos', 'SOS') : '—',
      alert: !!row.sos,
      unit: null,
      show: !!row.sos,
    },
  ];

  // Apply user pref: keep only visible cards (missing-key = visible),
  // reorder to match cardOrder (unknown new cards go to the end).
  const cards = (() => {
    const filtered = cardsRaw.filter((c) => prefs.isCardVisible(c.key));
    if (prefs.cardOrder.length === 0) return filtered;
    const byKey = new Map(filtered.map((c) => [c.key, c]));
    const ordered = [];
    prefs.cardOrder.forEach((k) => { if (byKey.has(k)) { ordered.push(byKey.get(k)); byKey.delete(k); } });
    byKey.forEach((c) => ordered.push(c));
    return ordered;
  })();

  const availableCards = cardsRaw.map((c) => ({
    id: c.key,
    label: typeof c.label === 'string' ? c.label : (c.key.charAt(0).toUpperCase() + c.key.slice(1)),
  }));

  const visible = cards.filter((c) => c.show);

  if (mode === 'docked' || mode === 'side') {
    return (
      <DockedStaffTelemetryRender
        collapsible={mode === 'docked'}
        prefs={prefs}
        cards={visible}
        availableCards={availableCards}
        row={row}
        status={status}
        pickerOpen={pickerOpen}
        setPickerOpen={setPickerOpen}
        close={close}
        t={t}
      />
    );
  }

  return (
    <div
      style={{
        ...frost(),
        borderTop: '1px solid var(--glass-border)',
        boxShadow: '0 -4px 24px rgba(0,0,0,0.06)',
      }}
    >
      {/* Header */}
      <div
        className="flex items-center justify-between px-4 py-2.5"
        style={{ borderBottom: '1px solid var(--glass-border)' }}
      >
        <div className="flex items-center gap-2.5">
          <span
            className="w-3 h-3 rounded-full"
            style={{ backgroundColor: status.color, boxShadow: `0 0 8px ${status.color}50` }}
          />
          <span
            className="w-6 h-6 rounded-full flex items-center justify-center"
            style={{ backgroundColor: `${status.color}20` }}
          >
            <User size={14} style={{ color: status.color }} />
          </span>
          <div>
            <h4 className="text-xs font-bold text-slate-800 dark:text-slate-200">{row.name ?? '—'}</h4>
            <p className="text-[10px] text-slate-500 capitalize">
              {(row.staffRole ?? 'other').replace(/_/g, ' ')}
            </p>
          </div>
          <span
            className="text-[9px] font-bold px-2 py-0.5 rounded-full text-white ms-1"
            style={{ backgroundColor: status.color, boxShadow: `0 2px 6px ${status.color}40` }}
          >
            {t(status.label)}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <div className="relative" data-telemetry-picker-root>
            <Tooltip content={t('telemetry.displaySettings', 'Telemetry display')}>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setPickerOpen((p) => !p); }}
                className={`p-1.5 rounded-lg transition-all ${
                  pickerOpen
                    ? 'bg-blue-500/15 text-blue-600'
                    : 'text-slate-400 hover:bg-white/40 hover:text-slate-600'
                }`}
                aria-label={t('telemetry.displaySettings', 'Telemetry display')}
                aria-expanded={pickerOpen}
                aria-haspopup="true"
              >
                <Settings2 size={14} />
              </button>
            </Tooltip>
            {pickerOpen && (
              // Floating mode: panel is anchored to the bottom of the
              // viewport, so the popover opens UPWARD to avoid being
              // clipped off the page edge.
              <div className="absolute bottom-full mb-2 end-0 z-50">
                <TelemetryCardPicker
                  availableCards={availableCards}
                  layout={prefs.layout}
                  setLayout={prefs.setLayout}
                  visibleCards={prefs.visibleCards}
                  isCardVisible={prefs.isCardVisible}
                  cardOrder={prefs.cardOrder}
                  setVisibility={prefs.setVisibility}
                  setCardOrder={prefs.setCardOrder}
                  onReset={prefs.reset}
                  onClose={() => setPickerOpen(false)}
                  t={t}
                />
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={close}
            className="p-1.5 rounded-lg hover:bg-white/40 text-slate-400 transition-all"
            aria-label="Close"
          >
            <X size={14} />
          </button>
        </div>
      </div>

      {/* Cards grid */}
      <div
        className={`p-3 grid gap-1.5 ${
          visible.length <= 6 ? 'grid-cols-3 sm:grid-cols-4 lg:grid-cols-6' : 'grid-cols-4 sm:grid-cols-5 lg:grid-cols-7'
        }`}
      >
        {visible.map((c, i) => (
          <Card key={i} {...c} />
        ))}
      </div>
    </div>
  );
}

function DockedStaffTelemetryRender({
  collapsible = true,
  prefs,
  cards,
  availableCards,
  row,
  status,
  pickerOpen,
  setPickerOpen,
  close,
  t,
}) {
  const collapsed = collapsible && prefs.collapsed;
  const setCollapsed = prefs.setCollapsed;

  const title = (
    <>
      <span
        className="w-2.5 h-2.5 rounded-full flex-shrink-0"
        style={{ backgroundColor: status.color, boxShadow: `0 0 6px ${status.color}50` }}
      />
      <span className="text-xs font-semibold truncate text-slate-700 dark:text-slate-200">
        <bdi>{row?.name ?? '—'}</bdi>
      </span>
    </>
  );

  return (
    <div className="px-3 py-2.5">
      {/* Header row */}
      <div className="flex items-center gap-2 mb-2">
        {collapsible ? (
          <button
            type="button"
            onClick={() => setCollapsed(!collapsed)}
            className="flex items-center gap-2 flex-1 min-w-0 text-start"
            aria-expanded={!collapsed}
          >
            {collapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
            {title}
            {collapsed && (
              <span className="text-[10px] text-slate-400 tabular-nums flex-shrink-0">
                {t('telemetry.cardCount', '{{n}} cards', { n: cards.length })}
              </span>
            )}
          </button>
        ) : (
          <div className="flex items-center gap-2 flex-1 min-w-0">{title}</div>
        )}
        <div className="relative flex-shrink-0" data-telemetry-picker-root>
          <Tooltip content={t('telemetry.displaySettings', 'Telemetry display')}>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setPickerOpen((p) => !p); }}
              className={`p-1 rounded-md transition-all ${
                pickerOpen ? 'bg-blue-500/15 text-blue-600' : 'text-slate-400 hover:bg-white/40'
              }`}
              aria-label={t('telemetry.displaySettings', 'Telemetry display')}
              aria-expanded={pickerOpen}
              aria-haspopup="true"
            >
              <Settings2 size={12} />
            </button>
          </Tooltip>
          {pickerOpen && (
            <div className="absolute top-full mt-2 end-0 z-50">
              <TelemetryCardPicker
                availableCards={availableCards}
                layout={prefs.layout}
                setLayout={prefs.setLayout}
                visibleCards={prefs.visibleCards}
                isCardVisible={prefs.isCardVisible}
                cardOrder={prefs.cardOrder}
                setVisibility={prefs.setVisibility}
                setCardOrder={prefs.setCardOrder}
                onReset={prefs.reset}
                onClose={() => setPickerOpen(false)}
                t={t}
              />
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={close}
          className="p-1 rounded-md text-slate-400 hover:bg-white/40 hover:text-slate-600 flex-shrink-0"
          aria-label={t('common.close', 'Close')}
        >
          <X size={12} />
        </button>
      </div>

      {/* Body: cards grid, only when expanded */}
      {!collapsed && (
        <div className="grid grid-cols-2 gap-2">
          {cards.map((card) => (
            <div key={card.key}>
              <Card {...card} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
